import { ReactElement } from 'react';
import { Menu } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { CASLAction, useAbility } from '@app/utils/authUtils';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BusinessIcon from '@mui/icons-material/Business';
import CallToActionIcon from '@mui/icons-material/CallToAction';
import ContactsIcon from '@mui/icons-material/Contacts';
import DevicesIcon from '@mui/icons-material/Devices';
// import EventIcon from '@mui/icons-material/Event';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SettingsIcon from '@mui/icons-material/Settings';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import { CollapseMenu } from './CollapseMenu';
import './styles.css';

const Icon = ({ IconComponent }: any) => {
  return <IconComponent sx={{ color: '#aeb2b7', '&.RaMenuItemLink-active': { color: 'inherit' } }} />;
};

interface ItemInterface {
  id: number;
  label: string;
  to?: string | null;
  leftIcon: ReactElement;
  children?: MenuItemInterface;
}

interface MenuItemInterface {
  [k: string]: ItemInterface;
}

export const CustomMenu = () => {
  const ability = useAbility();
  const navigate = useNavigate();

  let items: MenuItemInterface = {
    MEDIA: { id: 1, label: 'Media', to: '/media', leftIcon: <Icon IconComponent={OndemandVideoIcon} /> },
    EPG: { id: 2, label: 'EPG', to: '/epg', leftIcon: <Icon IconComponent={WatchLaterIcon} /> },
    COMPANIES: { id: 3, label: 'Companies', to: '/companies', leftIcon: <Icon IconComponent={BusinessIcon} /> },
    // PROMOTED_COMPANIES: { id: 3, label: 'Promoted Companies', to: '/prm-companies', leftIcon: <Icon IconComponent={BusinessIcon} /> },
    EXPERTS: { id: 4, label: 'Experts', to: '/experts', leftIcon: <Icon IconComponent={ContactsIcon} /> },
    SERIES: { id: 5, label: 'Series', to: '/series', leftIcon: <Icon IconComponent={SubscriptionsIcon} /> },
    CTA: { id: 6, label: 'CTA', to: '/ctas', leftIcon: <Icon IconComponent={CallToActionIcon} /> },
    EVENTS: { id: 7, label: 'Events', to: '/events', leftIcon: <Icon IconComponent={CallToActionIcon} /> },
    WEBSITE: {
      id: 9,
      label: 'Website',
      leftIcon: <Icon IconComponent={DevicesIcon} />,
      children: {
        RAILS: { id: 10, label: 'Rails', to: '/rails', leftIcon: <Icon IconComponent={ViewAgendaIcon} /> },
      },
    },
  };

  if (ability.can(CASLAction.GET, 'taxonomies')) {
    items.WEBSITE.children = {
      ...items.WEBSITE.children,
      TAXONOMY: {
        id: 11,
        label: 'Taxonomy',
        to: '/taxonomies',
        leftIcon: <Icon IconComponent={AccountTreeIcon} />,
      },
    };
  }

  if (ability.can(CASLAction.GET, 'questions')) {
    items = {
      ...items,
      //@ts-ignore
      QUESTIONS: { id: 8, label: 'Questions', to: '/questions', leftIcon: <Icon IconComponent={QuestionAnswerIcon} /> },
    };
  }

  if (ability.can(CASLAction.MANAGE, 'users')) {
    items = {
      ...items,
      //@ts-ignore
      SETTINGS: {
        id: 12,
        label: 'Settings',
        leftIcon: <Icon IconComponent={SettingsIcon} />,
        children: {
          USERS: {
            id: 13,
            label: 'Users',
            to: '/users',
            leftIcon: <Icon IconComponent={PeopleAltIcon} />,
          },
        },
      },
    };
  }

  //Sort menu by id
  const sortedItems: MenuItemInterface = Object.entries(items)
    .sort(([, a], [, b]) => a.id - b.id)
    .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

  const navigateToRoute = (url: string | null) => {
    url && navigate(url);
  };

  return (
    <Menu
      sx={{
        marginTop: 0,
        height: '100%',
        marginBottom: 0,

        borderRight: '1px solid rgba(0,0,0,0.1)',
      }}>
      <List sx={{ width: '100%' }}>
        {Object.keys(sortedItems).map((key: string) =>
          sortedItems[key as keyof typeof sortedItems].children ? (
            <CollapseMenu key={key} item={sortedItems[key]} navigateToRoute={navigateToRoute} />
          ) : (
            <ListItemButton
              key={key}
              id={key}
              onClick={() => navigateToRoute(sortedItems[key as keyof typeof sortedItems].to as string | null)}>
              <ListItemIcon>{sortedItems[key].leftIcon}</ListItemIcon>
              <ListItemText primary={sortedItems[key].label} />
            </ListItemButton>
          )
        )}
      </List>
      <div className="version-footer">
        <Typography sx={{ fontSize: 12 }}>&nbsp;Version: {process.env.APP_VERSION}</Typography>
      </div>
    </Menu>
  );
};
